"use client";

import * as React from "react";

import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { usePlasmicQueryData } from "@stadium/services/plasmic";

import { fetchPlasmicCMSData } from "@stadium/shared/src/helpers/plasmic/cms-api";

import CarouselContainer, { SlidesPerRow } from "@stadium/ui/src/components/carousel-container";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import {
  PLASMIC_T001_QUERY_KEY,
  T001_TABLE_ID,
  CMS_SECTION_FIELDS,
  TESTIMONIAL_FIELDS,
  T001TestimonialItem,
} from "./constants";

import styles from "./main.module.scss";

const T001_SLIDES_CONFIG: SlidesPerRow = {
  slidesPerRow: { slides: 4, rows: 1 },
  superLargeDesktop: { slides: 4, rows: 1 },
  web: { slides: 4, rows: 1 },
  ipad: { slides: 2, rows: 1 },
  tablet: { slides: 2, rows: 1 },
  mobile: { slides: 1, rows: 1 },
};

export interface T001Props {
  className?: string;
  testimonialData?: any;
}

const TestimonialCard = ({ item }: { item: T001TestimonialItem }) => (
  <div className={styles.card}>
    <div className={styles.cardContent}>
      {item[TESTIMONIAL_FIELDS.COMPANY_LOGO]?.url && (
        <Multimedia
          type="image"
          src={item[TESTIMONIAL_FIELDS.COMPANY_LOGO]?.url || ""}
          alt={item[TESTIMONIAL_FIELDS.COMPANY_LOGO_ALT] || "Company logo"}
          className={styles.companyLogo}
          style={
            item[TESTIMONIAL_FIELDS.COMPANY_LOGO_IMAGE_WIDTH]
              ? { width: item[TESTIMONIAL_FIELDS.COMPANY_LOGO_IMAGE_WIDTH] }
              : undefined
          }
        />
      )}
      <p className={styles.testimonialText}>{item[TESTIMONIAL_FIELDS.TESTIMONIAL_CONTENT]}</p>
    </div>
    <div className={styles.authorWrapper}>
      {item[TESTIMONIAL_FIELDS.AUTHOR_AVATAR]?.url && (
        <Multimedia
          type="image"
          src={item[TESTIMONIAL_FIELDS.AUTHOR_AVATAR]?.url || ""}
          alt={item[TESTIMONIAL_FIELDS.AUTHOR_AVATAR_ALT] || "Author avatar"}
          className={styles.authorAvatar}
        />
      )}
      <div>
        <h3 className={styles.authorName}>{item[TESTIMONIAL_FIELDS.AUTHOR_NAME]}</h3>
        <p className={styles.authorDesignation}>{item[TESTIMONIAL_FIELDS.AUTHOR_DESIGNATION]}</p>
      </div>
    </div>
  </div>
);

const T001: React.FC<Readonly<T001Props>> = ({ className, testimonialData }) => {
  const { isMobileDevice } = React.useContext(CurrentDeviceContext);
  const isTabletUp = !isMobileDevice;
  const testimonialCacheKey = testimonialData && JSON.stringify(testimonialData);

  const { data: cmsData } = usePlasmicQueryData(
    [PLASMIC_T001_QUERY_KEY, testimonialCacheKey],
    async () => {
      if (testimonialData?.[CMS_SECTION_FIELDS.TESTIMONIAL]) {
        return { testimonials: testimonialData[CMS_SECTION_FIELDS.TESTIMONIAL] };
      }

      const data = await fetchPlasmicCMSData({ tableId: T001_TABLE_ID });

      if (!data) return null;

      return {
        testimonials: data[CMS_SECTION_FIELDS.TESTIMONIAL],
      };
    },
  );

  const testimonials = (cmsData?.testimonials as T001TestimonialItem[]) || [];

  if (!testimonials.length) return null;

  const cards = testimonials.map((item) => (
    <TestimonialCard key={item[TESTIMONIAL_FIELDS.AUTHOR_NAME]} item={item} />
  ));

  return (
    <section className={`${styles.sectionWrapper} ${className || ""}`}>
      <div className={styles.contentWrapper}>
        {isTabletUp ? (
          <CarouselContainer
            slidesPerRowValues={T001_SLIDES_CONFIG}
            className={styles.carouselContainer}
          >
            {cards}
          </CarouselContainer>
        ) : (
          <div className={styles.mobileCardList}>{cards}</div>
        )}
      </div>
    </section>
  );
};

export default T001;
